import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../shared/config";
import { pageOfScreen } from "./navConfig";
import { estDirecteur } from "../lib/droits";
import { syncLues } from "./actualitesLues";

const TOUS_LES_GROUPES = ['Médecin', 'Assistant', 'Secrétaire'];
const ROLES_ASSIST = ['Assistant', 'Assistant(e)', 'Orthoptiste', 'Optométriste'];
const SEPT_JOURS = 7 * 86400000;

export const isActuGenerale = (a) => {
  if (a.groupe_cible === 'tous' || !a.groupe_cible) return true;
  if (Array.isArray(a.groupes_cibles)) return TOUS_LES_GROUPES.every(g => a.groupes_cibles.includes(g));
  return false;
};
export const isActuPourMoi = (a, role) => {
  if (a.groupe_cible === role) return true;
  if (Array.isArray(a.groupes_cibles)) return a.groupes_cibles.includes(role) && !isActuGenerale(a);
  return false;
};

// Même règle que l'ancienne page Actualités : stock ≤ seuil (ou < souhaité si pas de seuil)
const countSeuil = (arr) => (Array.isArray(arr) ? arr : []).filter(a => {
  const seuil = Number(a.seuil_alerte) || 0;
  if (seuil > 0) return (Number(a.nombre_en_stock) || 0) <= seuil;
  return (Number(a.nombre_souhaite) || 0) > (Number(a.nombre_en_stock) || 0);
}).length;

const get = (url) => axios.get(`${API}${url}`, { timeout: 15000 }).then(r => r.data).catch(() => null);
const len = (v) => (Array.isArray(v) ? v.length : 0);

// Une requête par chiffre, comme l'ancienne page Actualités (choix utilisateur).
export const useAujourdhui = (user, tree, corrHabilite) => {
  const [data, setData] = useState({ actus: [], gardes: [], anniversaires: [], todo: [], foot: null, effectif: null, loading: true });
  const role = user?.role;
  const isDir = estDirecteur(user, 'admin');
  const isMed = role === 'Médecin';
  const isAssist = ROLES_ASSIST.includes(role);
  const has = useCallback((s) => !!pageOfScreen(tree, s), [tree]);

  const refresh = useCallback(async () => {
    if (!user) return;
    const todo = [];
    const push = (id, icon, label, n, screen, ecran, hot = false) => { n = Number(n) || 0; if (n > 0 && has(screen)) todo.push({ id, icon, label, n, screen, ecran, hot }); };
    let foot = null;
    const jobs = [];

    const common = Promise.all([get('/actualites'), get('/centre-gardes/upcoming?days=60&limit=6'), get('/anniversaires')]);

    if (has('bloc-programmation')) jobs.push(get('/programmation/implants-alertes').then(d => {
      if (!d) return;
      if (d.show_commander) push('implants-commander', '📦', 'Implants à commander', d.a_commander_urgent, 'bloc-programmation', null, true);
      if (d.show_calculer) push('implants-calculer', '🧮', 'Implants à calculer', d.a_calculer, 'bloc-programmation', null, (d.a_calculer_urgent || 0) > 0);
    }));
    if (isDir) {
      jobs.push(get('/stocks-global/articles').then(d => push('stock-global', '🧪', 'Produits en stock global', countSeuil(d), 'stocks', null, true)));
      jobs.push(get('/stocks/articles').then(d => push('stock-local', '🧴', 'Produits à commander (centre)', countSeuil(d), 'stocks', null, true)));
    }
    if (isMed || isDir) jobs.push(get('/dossiers-a-traiter?statut=EN_ATTENTE').then(d => {
      const arr = (Array.isArray(d) ? d : []).filter(x => !isMed || x.medecin_id === user.id || x.transfere_a_id === user.id);
      const retard = arr.some(x => x.created_at && (Date.now() - new Date(x.created_at).getTime()) > SEPT_JOURS);
      push('courrier', '📥', isMed ? 'Courriers à traiter' : 'Dossiers à traiter', arr.length, 'a-traiter', 'COURRIER', retard);
    }));
    jobs.push(get('/dossiers-medicaux/notifications').then(d => push('suivi', '🩺', 'Suivis patient', len(d), 'a-traiter', 'DOSSIERS_MED')));
    jobs.push(get('/infos-chirurgie/count').then(d => { if (d?.alerte) push('chirurgie', '🔪', 'Dossiers chirurgie', d.count, 'a-traiter', 'CHIRURGIE'); }));
    if (isMed) jobs.push(get('/comptabilite/registre/notifications').then(d => push('factures', '🧾', 'Factures ouvertes', d?.count, 'registre')));
    if (isMed || isAssist || role === 'Secrétaire') jobs.push(get('/champ-visuel/notifications').then(d => push('cv', '👁️', 'Champs visuels à préparer', len(d), 'champ-visuel')));
    if (isMed || isAssist) jobs.push(get('/demandes-adressage').then(d => {
      const n = (Array.isArray(d) ? d : []).filter(x => x.type === 'interne' && x.medecin_cible_id === user.id && !x.vu && x.statut === 'en_attente').length;
      if (isMed) { if (n > 0 && has('demandes-examen')) foot = { label: `${n} demande${n > 1 ? 's' : ''} d'examen reçue${n > 1 ? 's' : ''}`, screen: 'demandes-examen' }; }
      else push('examens', '🔬', "Demandes d'examen", n, 'demandes-examen');
    }));
    if (!isMed && !isDir) jobs.push(Promise.all([get('/fichiers-a-signer/mes-attentes'), common, syncLues(user)]).then(([f, [actus], lues]) => {
      const aSigner = (Array.isArray(actus) ? actus : []).filter(a => a.signature_requise && (isActuGenerale(a) || isActuPourMoi(a, role)) && !(a.signatures || []).some(s => s.user_id === user.id) && !lues.includes(a.id)).length;
      push('a-signer', '✍️', 'À signer', len(f?.attentes) + aSigner, 'news');
    }));
    if (!isMed && !isDir) jobs.push(get('/messages/unread-counts').then(d => push('messages', '💬', 'Messages non lus', d?.total, 'messages')));
    if (role === 'Secrétaire' && user.autorisations?.conges?.droits_directeur) jobs.push(get('/conges').then(d => push('conges', '🏖️', 'Congés à valider', (Array.isArray(d) ? d : []).filter(c => c.statut === 'EN_ATTENTE').length, 'conges')));

    let effectif = null;
    if (isDir) jobs.push(get('/directeur/synthese-jour').then(d => {
      if (!d) return;
      effectif = d;
      push('jours', '📌', 'Demandes de jours', d.demandes_pending, 'demandes-travail');
    }));
    if (isMed || isDir) jobs.push(get('/boxes-disponibles').then(d => {
      const slots = (d?.slots || []).filter(s => !s.ferie);
      const libres = slots.filter(s => s.disponibles > 0).length;
      if (libres > 0 && slots.length && has('demandes-travail')) todo.push({ id: 'boxes', icon: '🚪', label: 'Créneaux libres', n: libres, suffix: `sur ${slots.length}`, screen: 'demandes-travail', hot: false, low: true });
    }));
    if (corrHabilite && has('correspondants')) jobs.push(get('/correspondants/dashboard').then(d => {
      const n = d?.stats?.nouvelles || 0;
      if (n > 0) foot = { label: `${n} demande${n > 1 ? 's' : ''} de correspondants`, screen: 'correspondants', hot: (d?.stats?.urgentes || 0) > 0 };
    }));

    const [[actus, gardes, anniv]] = await Promise.all([common, Promise.all(jobs)]);
    todo.sort((a, b) => (b.hot - a.hot) || ((a.low ? 1 : 0) - (b.low ? 1 : 0)) || (b.n - a.n));
    setData({
      actus: (Array.isArray(actus) ? actus : []).filter(a => isActuGenerale(a) || isActuPourMoi(a, role)),
      gardes: Array.isArray(gardes) ? gardes : [],
      anniversaires: (Array.isArray(anniv) ? anniv : []).filter(a => a.jours_restants <= 30).sort((a, b) => a.jours_restants - b.jours_restants),
      todo, foot, effectif, loading: false,
    });
  }, [user, has, isDir, isMed, isAssist, role, corrHabilite]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const id = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 120000);
    window.addEventListener('nav-badges-refresh', refresh);
    return () => { clearInterval(id); window.removeEventListener('nav-badges-refresh', refresh); };
  }, [refresh]);
  return data;
};
